import React, { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useGetActiveAnnouncementsForUser } from "../api/queries";
import AnnouncementModal from "./AnnouncementModal";

interface AnnouncementManagerProps {
  showAnnouncements: boolean;
  onComplete?: () => void;
}

export const AnnouncementManager: React.FC<AnnouncementManagerProps> = ({
  showAnnouncements,
  onComplete,
}) => {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);
  const [hasNavigated, setHasNavigated] = useState(false);

  // This hook uses showDismissed: "false" - filters dismissed announcements
  const { data: announcements = [], isLoading } =
    useGetActiveAnnouncementsForUser(showAnnouncements && !isDismissing);

  const hasAnnouncements = announcements.length > 0;

  useEffect(() => {
    if (
      showAnnouncements &&
      !isLoading &&
      hasAnnouncements &&
      !isModalOpen &&
      !isDismissing &&
      !hasNavigated
    ) {
      setIsModalOpen(true);
    }
  }, [
    showAnnouncements,
    isLoading,
    hasAnnouncements,
    announcements,
    isModalOpen,
    isDismissing,
    hasNavigated,
  ]);

  useEffect(() => {
    if (showAnnouncements && !isLoading && !hasAnnouncements && !isDismissing) {
      onComplete?.();
    }
  }, [
    showAnnouncements,
    isLoading,
    hasAnnouncements,
    onComplete,
    isDismissing,
  ]);

  const handleClose = async () => {
    setIsModalOpen(false);
    setIsDismissing(true);

    const currentPath = window.location.pathname;

    await new Promise((resolve) => setTimeout(resolve, 500));

    await queryClient.invalidateQueries({
      queryKey: ["announcements"],
    });

    setIsDismissing(false);

    const newPath = window.location.pathname;
    if (currentPath !== newPath) {
      setHasNavigated(true);
      onComplete?.();
    } else {
      onComplete?.();
    }
  };

  if (!showAnnouncements) {
    return null;
  }

  if (isLoading || isDismissing) {
    return null;
  }

  if (!isModalOpen || !hasAnnouncements || hasNavigated) {
    return null;
  }

  return (
    <AnnouncementModal
      open={isModalOpen}
      announcements={announcements}
      onClose={handleClose}
    />
  );
};
